import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';
import { API_CONFIG, ApiConfig } from '../api-config';
import {
  formatLadaSelectValue,
  resolveCountryFlagIconUrl,
} from '../../utils/country-flag.util';

export interface LadasSelectOption {
  value: string;
  label: string;
  iso?: string;
  iconUrl?: string;
}

/**
 * Ladas por país (con ISO para banderas).
 * GET {apiUrl}/selects/ladas → { success, data: [{ id, code, text, iconUrl? }] }
 */
@Injectable({ providedIn: 'root' })
export class LadasSelectService {
  private readonly httpClient = inject(HttpClient);
  private readonly apiConfig = inject<ApiConfig>(API_CONFIG);
  private readonly cache = new Map<string, LadasSelectOption[]>();
  private readonly endpointUrl = '/selects/ladas';

  getOptions(): Observable<LadasSelectOption[]> {
    const cached = this.cache.get(this.endpointUrl);
    if (cached) return of(cached);

    return this.httpClient
      .get(`${this.apiConfig.apiUrl}${this.endpointUrl}`, {
        observe: 'response',
        responseType: 'text',
      })
      .pipe(
        map((resp) => this.parseSelectResponse(resp.body)),
        tap((options) => this.cache.set(this.endpointUrl, options)),
        catchError(() => of([])),
      );
  }

  private parseSelectResponse(body: unknown): LadasSelectOption[] {
    if (typeof body !== 'string' || !body) return [];
    try {
      const parsed = JSON.parse(body) as {
        success?: boolean;
        data?: Array<{
          id: string;
          code?: string;
          text?: string;
          label?: string;
          iconUrl?: string;
        }>;
      };
      if (!parsed || parsed.success !== true || !Array.isArray(parsed.data)) {
        return [];
      }
      return parsed.data
        .map((item) => {
          const iso = item.code?.trim().toUpperCase() || undefined;
          const value = formatLadaSelectValue(item.id || '');
          const label =
            item.text?.trim() ||
            item.label?.trim() ||
            value;
          return {
            value,
            label,
            iso,
            iconUrl: resolveCountryFlagIconUrl({
              iso,
              iconUrl: item.iconUrl,
            }),
          } satisfies LadasSelectOption;
        })
        .filter((o) => !!o.value);
    } catch {
      return [];
    }
  }
}
